import { router, useForm, usePage } from "@inertiajs/react"
import { useEffect, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import type { CategoryOption, PoiMediaItem, PoiRow } from "@/lib/types"
import type { SharedProps } from "@/types"

interface Props {
  poi?: PoiRow
  categories: CategoryOption[]
  create_url?: string
  update_url?: string
  media_create_url?: string
  media?: PoiMediaItem[]
  onClose: () => void
}

interface FormData {
  name: string
  description: string
  lat: string
  lng: string
  address: string
  phone: string
  website_url: string
  booking_url: string
  google_maps_url: string
  category_ids: string[]
  metadata: {
    is_featured: boolean
    tags: string[]
  }
}

function PoiUploadForm({ mediaCreateUrl }: { mediaCreateUrl: string }) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, post, processing, reset } = useForm<{ file: File | null; alt_text: string }>({
    file:     null,
    alt_text: "",
  })

  function handleUpload(e: React.FormEvent) {
    e.preventDefault()
    post(mediaCreateUrl, {
      forceFormData: true,
      onSuccess:     () => reset(),
    })
  }

  return (
    <form onSubmit={handleUpload} className="space-y-2">
      <div>
        <Input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="cursor-pointer"
          onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
        />
        <p className="mt-1 text-xs text-muted-foreground">JPEG, PNG, or WebP — max 10 MB</p>
      </div>
      <Input
        placeholder="Alt text (optional)"
        value={data.alt_text}
        onChange={(e) => setData("alt_text", e.target.value)}
      />
      {errors.file && <p className="text-xs text-destructive">{errors.file}</p>}
      <Button type="submit" size="sm" disabled={!data.file || processing}>
        {processing ? "Uploading…" : "Upload photo"}
      </Button>
    </form>
  )
}

export function PoiForm({ poi, categories, create_url, update_url, media_create_url, media, onClose }: Props) {
  const { errors } = usePage<SharedProps>().props
  const [tagsInput, setTagsInput] = useState((poi?.tags ?? []).join(", "))
  const [coverMediaId, setCoverMediaId] = useState<number | null>(
    media && media.length > 0 ? media[0].id : null
  )

  useEffect(() => {
    if (media && media.length > 0) setCoverMediaId(media[0].id)
  }, [media])

  function handleSetCover(m: PoiMediaItem) {
    if (m.id === coverMediaId) return
    const snapshot = coverMediaId
    setCoverMediaId(m.id)
    router.patch(
      m.set_cover_url,
      {},
      { preserveScroll: true, onError: () => setCoverMediaId(snapshot) }
    )
  }

  const { data, setData, post, patch, processing } = useForm<FormData>({
    name:            poi?.name ?? "",
    description:     poi?.description ?? "",
    lat:             poi?.lat != null ? String(poi.lat) : "",
    lng:             poi?.lng != null ? String(poi.lng) : "",
    address:         poi?.address ?? "",
    phone:           poi?.phone ?? "",
    website_url:     poi?.website_url ?? "",
    booking_url:     poi?.booking_url ?? "",
    google_maps_url: poi?.google_maps_url ?? "",
    category_ids:    poi?.categories.map((c) => String(c.id)) ?? [],
    metadata: {
      is_featured: poi?.is_featured ?? false,
      tags:        poi?.tags ?? [],
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (poi) {
      patch(update_url!, { onSuccess: onClose })
    } else {
      post(create_url!, { onSuccess: onClose })
    }
  }

  function toggleCategory(id: string) {
    const next = data.category_ids.includes(id)
      ? data.category_ids.filter((cid) => cid !== id)
      : [...data.category_ids, id]
    setData("category_ids", next)
  }

  function handleTagsChange(value: string) {
    setTagsInput(value)
    const tags = value
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
    setData("metadata", { ...data.metadata, tags })
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* Photos section — shown above form fields when editing */}
      {poi && media !== undefined && (
        <section className="mb-2">
          <h3 className="mb-3 text-sm font-semibold">Photos</h3>
          <div className="mb-4 grid grid-cols-2 gap-3">
            {media.map((m) => {
              const isCover = m.id === coverMediaId
              return (
                <div key={m.id} className="group relative aspect-square">
                  <img
                    src={m.url ?? ""}
                    alt={m.alt_text ?? ""}
                    className="h-full w-full rounded-md object-cover"
                  />
                  {isCover && (
                    <span className="absolute bottom-1 left-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                      Cover
                    </span>
                  )}
                  {!isCover && (
                    <button
                      type="button"
                      className="absolute bottom-1 left-1 rounded bg-black/50 px-1.5 py-0.5 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100"
                      onClick={() => handleSetCover(m)}
                    >
                      Set as cover
                    </button>
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute right-1 top-1 size-7 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={() => {
                      if (!window.confirm("Delete this photo?")) return
                      router.delete(m.delete_url)
                    }}
                    aria-label="Delete photo"
                  >
                    ×
                  </Button>
                </div>
              )
            })}
            {media.length === 0 && (
              <p className="col-span-2 text-sm text-muted-foreground">No photos yet.</p>
            )}
          </div>
          <PoiUploadForm mediaCreateUrl={media_create_url!} />
        </section>
      )}

      {/* POI metadata form */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="poi-name">Name *</Label>
          <Input
            id="poi-name"
            value={data.name}
            onChange={(e) => setData("name", e.target.value)}
            required
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="poi-description">Description</Label>
          <Textarea
            id="poi-description"
            value={data.description}
            onChange={(e) => setData("description", e.target.value)}
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="poi-lat">Latitude</Label>
            <Input
              id="poi-lat"
              type="number"
              step="any"
              placeholder="38.7223"
              value={data.lat}
              onChange={(e) => setData("lat", e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="poi-lng">Longitude</Label>
            <Input
              id="poi-lng"
              type="number"
              step="any"
              placeholder="-9.1393"
              value={data.lng}
              onChange={(e) => setData("lng", e.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="poi-address">Address</Label>
          <Input
            id="poi-address"
            value={data.address}
            onChange={(e) => setData("address", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="poi-phone">Phone</Label>
            <Input
              id="poi-phone"
              type="tel"
              value={data.phone}
              onChange={(e) => setData("phone", e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="poi-website">Website</Label>
            <Input
              id="poi-website"
              type="url"
              value={data.website_url}
              onChange={(e) => setData("website_url", e.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="poi-booking">Booking URL</Label>
          <Input
            id="poi-booking"
            type="url"
            value={data.booking_url}
            onChange={(e) => setData("booking_url", e.target.value)}
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="poi-maps">Google Maps URL</Label>
          <Input
            id="poi-maps"
            type="url"
            value={data.google_maps_url}
            onChange={(e) => setData("google_maps_url", e.target.value)}
          />
        </div>

        {categories.length > 0 && (
          <div className="grid gap-2">
            <Label>Categories</Label>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => {
                const selected = data.category_ids.includes(String(cat.id))
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(String(cat.id))}
                    className="focus:outline-none"
                  >
                    <Badge
                      variant={selected ? "default" : "outline"}
                      className="cursor-pointer"
                    >
                      {cat.icon && <span className="mr-1">{cat.icon}</span>}
                      {cat.name}
                    </Badge>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <div className="grid gap-1.5">
          <Label htmlFor="poi-tags">Tags (comma-separated)</Label>
          <Input
            id="poi-tags"
            placeholder="cafe, rooftop, view"
            value={tagsInput}
            onChange={(e) => handleTagsChange(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            id="poi-featured"
            type="checkbox"
            checked={data.metadata.is_featured}
            onChange={(e) =>
              setData("metadata", { ...data.metadata, is_featured: e.target.checked })
            }
            className="h-4 w-4 rounded border-border"
          />
          <Label htmlFor="poi-featured" className="cursor-pointer font-normal">
            Featured POI
          </Label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? "Saving…" : poi ? "Save changes" : "Create POI"}
          </Button>
        </div>
      </form>
    </div>
  )
}
